import jsPDF from "jspdf";
import { FAVICON_BASE64 } from "./faviconBase64";

// Color palette
const BLUE = [26, 86, 219];
const BLUE_DK = [14, 58, 153];
const BLUE_LT = [235, 242, 255];
const INK = [15, 25, 35];
const MID = [74, 85, 104];
const MUTED = [138, 155, 176];
const LINE = [226, 234, 244];
const BG = [247, 250, 253];
const WHITE = [255, 255, 255];
const WASTE_BG = [254, 243, 199];
const WASTE_TXT = [146, 64, 14];
const WASTE_BRD = [253, 230, 138];

// Layout (A4: 210x297mm)
const ML = 22;
const MR = 22;
const PW = 210;
const CW = PW - ML - MR; // 166
const PH = 297;
const FOOTER_Y = PH - 16;
const MAX_Y = FOOTER_Y - 4;

// Rounded page constants
const PM = 3;       // page margin for card effect
const PMW = PW - 2 * PM; // 204
const R = 3.5;      // corner radius

export function generatePDF(projectName, formData, results, roomType, toolData) {
  const doc = new jsPDF();
  let y = 0;

  const ensureSpace = (needed) => {
    if (y + needed > MAX_Y) {
      doc.addPage();
      // New page: fill BG + white body
      doc.setFillColor(...BG);
      doc.rect(0, 0, PW, PH, "F");
      doc.setFillColor(...WHITE);
      doc.rect(PM, PM, PMW, PH - 2 * PM, "F");
      y = 20;
    }
  };

  // ============ PAGE 1 BACKGROUND ============
  // Fill entire page with BG for card effect
  doc.setFillColor(...BG);
  doc.rect(0, 0, PW, PH, "F");

  // White body area (below header, above footer)
  doc.setFillColor(...WHITE);
  doc.rect(PM, PM, PMW, PH - 2 * PM, "F");

  // ============ HEADER (rounded top corners) ============
  const HEADER_H = 56;
  // Band boundaries (relative to PM)
  const band1End = PM + HEADER_H * 0.55;   // ~34.8
  const band2End = PM + HEADER_H * 0.80;   // ~47.8
  const band3End = PM + HEADER_H;           // ~59

  // Dark blue: full header as rounded rect (all corners), then square off bottom
  doc.setFillColor(11, 45, 130);
  doc.roundedRect(PM, PM, PMW, HEADER_H, R, R, "F");
  doc.rect(PM, PM + HEADER_H - R, PMW, R, "F");

  // Medium blue band
  doc.setFillColor(18, 60, 170);
  doc.rect(PM, band1End, PMW, band2End - band1End, "F");

  // Light blue band
  doc.setFillColor(...BLUE);
  doc.rect(PM, band2End, PMW, band3End - band2End, "F");

  // --- Logo icon (white rounded rect + favicon) ---
  doc.setFillColor(...WHITE);
  doc.roundedRect(ML, PM + 7, 13, 13, 2.8, 2.8, "F");
  try {
    if (FAVICON_BASE64 && FAVICON_BASE64.length > 100) {
      doc.addImage(FAVICON_BASE64, "JPEG", ML + 1.8, PM + 8.8, 9.4, 9.4);
    }
  } catch (e) { /* fallback */ }

  // "PLANIMETRIK" title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...WHITE);
  doc.text("PLANIMETRIK", ML + 17, PM + 14.5);

  // "INFORME DE CALCULO" subtitle
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(170, 190, 225);
  doc.text("INFORME DE CÁLCULO", ML + 17, PM + 19.5);

  // Date badge (right side)
  const dateStr = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...WHITE);
  const dateW = doc.getTextWidth(dateStr) + 10;
  const dateX = PW - MR - dateW;
  doc.setFillColor(35, 70, 165);
  doc.roundedRect(dateX, PM + 9, dateW, 8, 4, 4, "F");
  doc.setDrawColor(75, 115, 200);
  doc.setLineWidth(0.3);
  doc.roundedRect(dateX, PM + 9, dateW, 8, 4, 4, "S");
  doc.text(dateStr, dateX + 5, PM + 14.5);

  // --- Project name: centered vertically in MEDIUM blue band ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...WHITE);
  const pName = projectName || "Sin nombre";
  let displayName = pName;
  const maxNameW = CW - 10;
  while (doc.getTextWidth(displayName) > maxNameW && displayName.length > 3) {
    displayName = displayName.slice(0, -1);
  }
  if (displayName !== pName) displayName += "...";
  // Medium band: band1End to band2End. Center text baseline there.
  const nameFontH = 20 * 0.353; // ~7mm
  const nameY = band1End + (band2End - band1End) / 2 + nameFontH / 2 - 0.5;
  doc.text(displayName, ML, nameY);

  // --- Meta row: centered vertically in LIGHT blue band ---
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(190, 210, 235);
  const metaFontH = 9.5 * 0.353; // ~3.3mm
  const metaY = band2End + (band3End - band2End) / 2 + metaFontH / 2 - 0.3;
  let mx = ML;
  if (roomType) {
    doc.text(roomType, mx, metaY);
    mx += doc.getTextWidth(roomType) + 12;
  }
  if (results && results.m2_netos) {
    doc.text(results.m2_netos + " m\u00B2 netos", mx, metaY);
  }

  y = PM + HEADER_H + 10;

  // ============ SECTION HELPERS ============
  const drawSectionLabel = (title) => {
    ensureSpace(20);
    doc.setFillColor(...BLUE);
    doc.circle(ML + 1.5, y - 1, 1.3, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...BLUE);
    doc.text(title.toUpperCase(), ML + 6, y);
    y += 8;
  };

  const drawSectionDivider = () => {
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(ML, y, PW - MR, y);
    y += 8;
  };

  const drawInfoRow = (key, val, isAccent, isLast) => {
    ensureSpace(9);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MID);
    doc.text(String(key), ML, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...(isAccent ? BLUE : INK));
    doc.text(String(val), PW - MR, y, { align: "right" });
    if (!isLast) {
      doc.setDrawColor(...LINE);
      doc.setLineWidth(0.15);
      doc.setLineDashPattern([1.5, 1.5], 0);
      doc.line(ML, y + 3, PW - MR, y + 3);
      doc.setLineDashPattern([], 0);
    }
    y += 8;
  };

  // Tool-style group box helper (reused for tools and nivelacion)
  const drawGroupBox = (title, items, startX, startY, boxW, titleColor) => {
    const tColor = titleColor || BLUE;
    // Calculate height
    let lineCount = 0;
    items.forEach((t) => {
      lineCount += doc.splitTextToSize("- " + t, boxW - 10).length;
    });
    const boxH = 12 + lineCount * 4.5 + 4;

    ensureSpace(boxH + 4);
    doc.setFillColor(...BG);
    doc.roundedRect(startX, startY, boxW, boxH, 2.5, 2.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...tColor);
    doc.text(title, startX + 5, startY + 7);

    let ty = startY + 13;
    items.forEach((t) => {
      const lines = doc.splitTextToSize("- " + t, boxW - 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...MID);
      lines.forEach((line) => {
        doc.text(line, startX + 5, ty);
        ty += 4.5;
      });
    });
    return boxH;
  };

  const calcGroupBoxHeight = (items, boxW) => {
    if (!items || items.length === 0) return 0;
    let lineCount = 0;
    items.forEach((t) => {
      lineCount += doc.splitTextToSize("- " + t, boxW - 10).length;
    });
    return 12 + lineCount * 4.5 + 4;
  };

  // ============ MATERIAL SECTION ============
  if (formData && formData.material_type) {
    drawSectionLabel("Material seleccionado");
    const cardGap = 4;
    const matDisplay = (formData.material_type || "-").replace(/_/g, " ").replace(/^./, c => c.toUpperCase());
    const items = [
      { label: "TIPO", value: matDisplay },
      { label: "FORMATO", value: formData.format_type || "-" },
      { label: "INSTALACIÓN", value: formData.installation_type || "-" },
    ];
    if (formData.installation_pattern && formData.format_type === "Lama") {
      items.push({ label: "PATRÓN", value: formData.installation_pattern });
    }
    const cols = Math.min(items.length, 3);
    const actualCardW = (CW - cardGap * (cols - 1)) / cols;
    const cardH = 18;
    ensureSpace(cardH + 6);
    items.forEach((item, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const cx = ML + col * (actualCardW + cardGap);
      const cy = y + row * (cardH + cardGap);
      doc.setFillColor(...BLUE_LT);
      doc.roundedRect(cx, cy, actualCardW, cardH, 2.5, 2.5, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text(item.label, cx + 5, cy + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(...INK);
      let val = item.value;
      while (doc.getTextWidth(val) > actualCardW - 10 && val.length > 3) val = val.slice(0, -1);
      doc.text(val, cx + 5, cy + 14);
    });
    y += Math.ceil(items.length / cols) * (cardH + cardGap) + 4;
  }

  // ============ RESULTS SECTION ============
  if (results) {
    drawSectionDivider();
    drawSectionLabel("Resultados del cálculo");

    // KPI cards
    const kpiGap = 5;
    const kpiW = (CW - kpiGap) / 2;
    const kpiH = 26;
    ensureSpace(kpiH + 20);

    // Primary KPI: m2 totales
    doc.setFillColor(...BLUE_LT);
    doc.setDrawColor(...BLUE);
    doc.setLineWidth(0.5);
    doc.roundedRect(ML, y, kpiW, kpiH, 3, 3, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...BLUE);
    doc.text("M\u00B2 TOTALES", ML + 6, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...BLUE_DK);
    doc.text(String(results.m2_totales), ML + 6, y + 17);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("metros cuadrados necesarios", ML + 6, y + 23);

    // Secondary KPI: boxes or rolls
    const kpi2X = ML + kpiW + kpiGap;
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.4);
    doc.roundedRect(kpi2X, y, kpiW, kpiH, 3, 3, "FD");

    if (results.boxes) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text("CAJAS NECESARIAS", kpi2X + 6, y + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.setTextColor(...INK);
      doc.text(String(results.boxes.total), kpi2X + 6, y + 17);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text(results.boxes.m2_per_box + " m\u00B2/caja = " + results.boxes.total_m2_supplied + " m\u00B2", kpi2X + 6, y + 23);
    } else if (results.rolls) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text("ROLLOS NECESARIOS", kpi2X + 6, y + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.setTextColor(...INK);
      doc.text(String(results.rolls.total), kpi2X + 6, y + 17);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text(results.rolls.roll_m2 + " m\u00B2/rollo", kpi2X + 6, y + 23);
    }

    y += kpiH + 6;

    // m2 netos box + waste pill
    ensureSpace(16);
    const netosW = CW * 0.52;
    doc.setFillColor(...BG);
    doc.roundedRect(ML, y, netosW, 11, 2, 2, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MID);
    doc.text("m\u00B2 netos", ML + 5, y + 7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text(results.m2_netos + " m\u00B2", ML + netosW - 5, y + 7, { align: "right" });

    // Waste pill (FIX: arrow -> and tight width)
    const wasteTxt = "+" + results.waste_percent + "% desperdicio - " + results.m2_desperdicio + " m\u00B2";
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    const wastePillW = doc.getTextWidth(wasteTxt) + 10;
    const pillX = ML + netosW + 5;
    const pillMaxW = Math.min(wastePillW, CW - netosW - 5);
    doc.setFillColor(...WASTE_BG);
    doc.roundedRect(pillX, y + 1, pillMaxW, 9, 4.5, 4.5, "F");
    doc.setDrawColor(...WASTE_BRD);
    doc.setLineWidth(0.3);
    doc.roundedRect(pillX, y + 1, pillMaxW, 9, 4.5, 4.5, "S");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...WASTE_TXT);
    doc.text(wasteTxt, pillX + 5, y + 7);
    y += 16;
  }

  // ============ ADHESIVE + RODAPIE ============
  if (results) {
    const hasAdhesive = results.adhesive;
    const hasRodapie = results.rodapie;

    if (hasAdhesive || hasRodapie) {
      drawSectionDivider();
      drawSectionLabel("Fijación y Acabados");

      if (hasAdhesive && results.adhesive.type !== "base_aislante") {
        if (results.adhesive.type === "none") {
          const instText = formData ? formData.installation_type || "" : "";
          drawInfoRow("Adhesivo / Fijación", "Sin adhesivo - instalación " + instText.toLowerCase());
        } else if (results.adhesive.type === "adhesive") {
          drawInfoRow("Adhesivo / Fijación", results.adhesive.name);
          drawInfoRow("Kg totales", results.adhesive.kg_total + " kg");
          const cRaw = results.adhesive.container_name || "Envase";
          const cPlural = cRaw.replace(/ón$/, "on") + "es";
          const cName = cPlural + " de " + results.adhesive.container_size + " " + (results.adhesive.container_unit || "");
          drawInfoRow(cName, String(results.adhesive.containers));
        } else if (results.adhesive.type === "tape") {
          drawInfoRow("Adhesivo / Fijación", results.adhesive.name);
          drawInfoRow("Ml totales", results.adhesive.ml_total + " ml");
          drawInfoRow("Rollos de " + results.adhesive.container_size + " " + (results.adhesive.container_unit || ""), String(results.adhesive.rolls));
        } else if (results.adhesive.type === "hardware" && Array.isArray(results.adhesive.items)) {
          results.adhesive.items.forEach((item) => {
            if (!item) return;
            drawInfoRow(item.name, item.quantity + " " + (item.unit || ""));
            if (item.containers) {
              const itemPlural = ((item.container_name || "Envase").replace(/ón$/, "on")) + "es";
              drawInfoRow("  " + itemPlural, item.containers + " (de " + item.container_size + " " + (item.unit || "") + ")");
            }
          });
        } else if (results.adhesive.type === "tapes") {
          drawInfoRow("Stickers necesarios", String(results.adhesive.stickers));
          drawInfoRow("Rollos (" + results.adhesive.stickers_per_roll + "/rollo)", String(results.adhesive.rolls));
        }
      }

      // Rodapie (FIX: arrow -> instead of unicode)
      if (hasRodapie) {
        const rodaInfo = results.rodapie.with_waste_m + " ml (+7% - base " + results.rodapie.length_m + " ml)";
        drawInfoRow("Rodapié - metros lineales", rodaInfo, true);
        drawInfoRow("Tiras de " + results.rodapie.strip_length + " m", results.rodapie.strips + " tiras", false, true);
      }
    }
  }

  // ============ WELDING CORD ============
  if (results && results.welding_cord) {
    drawSectionDivider();
    drawSectionLabel("Soldadura Térmica");
    drawInfoRow("Tipo", results.welding_cord.type);
    drawInfoRow("Rollos de " + results.welding_cord.cord_size + " ml", String(results.welding_cord.rolls));
    if (results.welding_cord.total_ml) {
      drawInfoRow("Metros lineales totales", results.welding_cord.total_ml + " ml", false, true);
    }
  }

  // ============ ESD ============
  if (results && results.esd) {
    drawSectionDivider();
    drawSectionLabel("Pavimento ESD");
    drawInfoRow("Cinta de cobre (ml necesarios)", results.esd.ml_needed + " ml");
    drawInfoRow("Rollos de 33 ml", String(results.esd.rolls_33ml));
    drawInfoRow("Ml suministrados", results.esd.ml_supplied + " ml", false, true);
  }

  // ============ LEVELING (restructured as group boxes) ============
  if (results && results.leveling) {
    drawSectionDivider();
    drawSectionLabel("Nivelación del soporte");

    const tGap = 6;
    const tW = (CW - tGap) / 2;

    // Group 1: MEDICION
    const medicionItems = [
      "Espesor: " + results.leveling.thickness_mm + " mm",
    ];
    // Group 2: MEZCLADO
    const mezcladoItems = [
      "Autonivelante: " + results.leveling.autonivelante_kg + " kg",
      "Sacos de 25 kg: " + results.leveling.bags_25kg + " sacos",
    ];

    const medH = calcGroupBoxHeight(medicionItems, tW);
    const mezH = calcGroupBoxHeight(mezcladoItems, tW);
    const row1H = Math.max(medH, mezH);

    ensureSpace(row1H + 6);
    const row1Y = y;

    // Draw medicion (left)
    doc.setFillColor(...BG);
    doc.roundedRect(ML, row1Y, tW, row1H, 2.5, 2.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BLUE);
    doc.text("MEDICIÓN", ML + 5, row1Y + 7);
    let ty1 = row1Y + 13;
    medicionItems.forEach((t) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...MID);
      doc.text("- " + t, ML + 5, ty1);
      ty1 += 4.5;
    });

    // Draw mezclado (right)
    const mezX = ML + tW + tGap;
    doc.setFillColor(...BG);
    doc.roundedRect(mezX, row1Y, tW, row1H, 2.5, 2.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BLUE);
    doc.text("MEZCLADO", mezX + 5, row1Y + 7);
    let ty2 = row1Y + 13;
    mezcladoItems.forEach((t) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...MID);
      doc.text("- " + t, mezX + 5, ty2);
      ty2 += 4.5;
    });

    y = row1Y + row1H + 5;

    // Group 3: REPLANTEO (full width)
    const replanteoItems = [
      "Imprimación: " + results.leveling.primer_kg + " kg",
      "Bidones de 5 kg: " + results.leveling.drums_5kg + " bidones",
    ];
    const repH = calcGroupBoxHeight(replanteoItems, CW);
    ensureSpace(repH + 4);
    doc.setFillColor(...BG);
    doc.roundedRect(ML, y, CW, repH, 2.5, 2.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BLUE);
    doc.text("REPLANTEO", ML + 5, y + 7);
    let ty3 = y + 13;
    replanteoItems.forEach((t) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...MID);
      doc.text("- " + t, ML + 5, ty3);
      ty3 += 4.5;
    });
    y += repH + 5;
  }

  // ============ BASE AISLANTE ============
  if (results && results.base_aislante) {
    drawSectionDivider();
    drawSectionLabel("Base Aislante");
    drawInfoRow(
      "Base aislante",
      results.base_aislante.total_m2_supplied + " m\u00B2 (" + results.base_aislante.rollos + " rollos de " + results.base_aislante.m2_per_roll + " m\u00B2)",
      false,
      true
    );
  }

  // ============ TOOLS ============
  if (toolData) {
    drawSectionDivider();
    drawSectionLabel("Herramientas necesarias");

    const drawToolList = (items, startX, startY, maxW) => {
      let ty = startY;
      if (!items || !Array.isArray(items)) return ty;
      items.forEach((t) => {
        const lines = doc.splitTextToSize("- " + t, maxW - 10);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...MID);
        lines.forEach((line) => {
          doc.text(line, startX + 5, ty);
          ty += 4.5;
        });
      });
      return ty;
    };

    const calcToolHeight = (items, maxW) => {
      if (!items || !Array.isArray(items) || items.length === 0) return 0;
      let lineCount = 0;
      items.forEach((t) => {
        lineCount += doc.splitTextToSize("- " + t, maxW - 10).length;
      });
      return 12 + lineCount * 4.5 + 4;
    };

    if (toolData.main) {
      const tGap = 6;
      const tW = (CW - tGap) / 2;

      const medH = toolData.main.medición ? calcToolHeight(toolData.main.medición, tW) : 0;
      const corteH = toolData.main.corte ? calcToolHeight(toolData.main.corte, tW) : 0;
      const maxH = Math.max(medH, corteH);

      if (maxH > 0) {
        ensureSpace(maxH + 6);
        const rowY = y;

        if (toolData.main.medición && toolData.main.medición.length > 0) {
          doc.setFillColor(...BG);
          doc.roundedRect(ML, rowY, tW, maxH, 2.5, 2.5, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLUE);
          doc.text("MEDICIÓN Y REPLANTEO", ML + 5, rowY + 7);
          drawToolList(toolData.main.medición, ML, rowY + 13, tW);
        }

        if (toolData.main.corte && toolData.main.corte.length > 0) {
          const cx = ML + tW + tGap;
          doc.setFillColor(...BG);
          doc.roundedRect(cx, rowY, tW, maxH, 2.5, 2.5, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...BLUE);
          doc.text("CORTE, AJUSTE Y COLOCACIÓN", cx + 5, rowY + 7);
          drawToolList(toolData.main.corte, cx, rowY + 13, tW);
        }
        y = rowY + maxH + 5;
      }

      if (toolData.main.acabado && toolData.main.acabado.length > 0) {
        const acabH = calcToolHeight(toolData.main.acabado, CW);
        ensureSpace(acabH + 4);
        doc.setFillColor(...BG);
        doc.roundedRect(ML, y, CW, acabH, 2.5, 2.5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...BLUE);
        doc.text("ACABADO Y REMATES", ML + 5, y + 7);
        drawToolList(toolData.main.acabado, ML, y + 13, CW);
        y += acabH + 5;
      }
    }

    // Welding tools
    if (toolData.welding && toolData.welding.length > 0) {
      const calcH = (items, maxW) => {
        let lc = 0;
        items.forEach((t) => { lc += doc.splitTextToSize("- " + t, maxW - 10).length; });
        return 12 + lc * 4.5 + 4;
      };
      const wH = calcH(toolData.welding, CW);
      ensureSpace(wH + 4);
      doc.setFillColor(...BG);
      doc.roundedRect(ML, y, CW, wH, 2.5, 2.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(255, 165, 0);
      doc.text("SOLDADURA TÉRMICA", ML + 5, y + 7);
      let ty = y + 13;
      toolData.welding.forEach((t) => {
        const lines = doc.splitTextToSize("- " + t, CW - 10);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(...MID);
        lines.forEach((line) => { doc.text(line, ML + 5, ty); ty += 4.5; });
      });
      y += wH + 5;
    }

    // Leveling tools (titles bold, no dash, no brackets; items with dash)
    if (toolData.leveling && typeof toolData.leveling === "object") {
      const entries = Object.entries(toolData.leveling).filter(([, items]) => Array.isArray(items));
      if (entries.length > 0) {
        // Calculate total height: header + each category title + items
        let totalLines = 0;
        entries.forEach(([, items]) => {
          totalLines += 1; // title line
          items.forEach((t) => { totalLines += doc.splitTextToSize("- " + t, CW - 10).length; });
        });
        const lvH = 12 + totalLines * 4.5 + 4;
        ensureSpace(lvH + 4);
        doc.setFillColor(...BG);
        doc.roundedRect(ML, y, CW, lvH, 2.5, 2.5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...BLUE);
        doc.text("NIVELACIÓN", ML + 5, y + 7);
        let ty = y + 13;
        entries.forEach(([cat, items]) => {
          // Category title: bold, capitalized, no dash, no brackets
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8.5);
          doc.setTextColor(...INK);
          doc.text(cat.charAt(0).toUpperCase() + cat.slice(1), ML + 5, ty);
          ty += 4.5;
          // Items: normal, with dash
          items.forEach((t) => {
            const lines = doc.splitTextToSize("- " + t, CW - 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            doc.setTextColor(...MID);
            lines.forEach((line) => { doc.text(line, ML + 5, ty); ty += 4.5; });
          });
        });
        y += lvH + 5;
      }
    }
  }

  // ============ FOOTER (all pages, rounded bottom corners) ============
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Footer background with rounded bottom corners
    const footerH = PH - PM - (FOOTER_Y - 2);
    doc.setFillColor(...BG);
    doc.roundedRect(PM, FOOTER_Y - 2, PMW, footerH, R, R, "F");
    // Square off top corners of footer
    doc.rect(PM, FOOTER_Y - 2, PMW, R, "F");

    // Separator line
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(ML, FOOTER_Y, PW - MR, FOOTER_Y);

    // Brand text
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("A ", ML, FOOTER_Y + 7);
    const aW = doc.getTextWidth("A ");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BLUE);
    doc.text("Ventasuelos SL", ML + aW, FOOTER_Y + 7);
    const vsW = doc.getTextWidth("Ventasuelos SL");
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(" Company - planimetrik.com", ML + aW + vsW, FOOTER_Y + 7);
    // Page number
    doc.text("Página " + i + " de " + pageCount, PW - MR, FOOTER_Y + 7, { align: "right" });
  }

  // ============ DOWNLOAD ============
  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);
  const fileName = (projectName || "planimetrik") + "_cálculo.pdf";

  // Try download via link click, fallback to window.open
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
